import {AbstractControl, ValidatorFn, Validators} from '@angular/forms';
import {CustomValidator} from 'src/app/modules/common';
import {ApplicationRowDTO} from 'src/app/modules/onboarding-admin-portal/models/application/ApplicationRowDTO';
import {ApplicationUtil} from './ApplicationUtil';
import {DateUtil} from 'src/app/modules/common/util/DateUtil';
import {
  ApplicationRowReferenceData
} from '../components/obd-bulk-application-import/obd-bulk-application-import.component';
import {ReferenceDTO} from '../../../onboarding-admin-portal/models/application/ReferenceDTO';


export enum GlobalValidationError {
  DuplicateIdentification = 'duplicateIdentification',
  MissingPrincipal = 'missingPrincipal',
  MultiplePrincipal = 'multiplePrincipal',
  DateValidation = 'dateValidation',
  ReferenceDataValidation = 'referenceDataValidation',
  GeneralValidation = 'generalValidation',
}

export class ApplicationImportValidationUtil {
  static validate(applicationRows: ApplicationRowDTO[], isDirectPayingMember: boolean, referenceData: ApplicationRowReferenceData): ApplicationRowDTO[] {
    applicationRows = this.clearPreviousErrors(applicationRows);
    applicationRows = this.validateDuplicateIdentification(applicationRows);
    applicationRows = this.validatePrincipalMemberRules(applicationRows);
    applicationRows = this.validateReferenceData(applicationRows, referenceData);
    applicationRows = this.validateGeneralValidationOnAllRows(applicationRows, isDirectPayingMember);
    return applicationRows;
  }

  static clearPreviousErrors(applicationRows: ApplicationRowDTO[]): ApplicationRowDTO[] {
    for (const row of applicationRows) {
      row.validationErrors = [];
    }
    return applicationRows;
  }

  static validateDuplicateIdentification(applicationRows: ApplicationRowDTO[]): ApplicationRowDTO[] {
    const seen = new Map<string, ApplicationRowDTO[]>();
    for (const row of applicationRows) {
      const key = `${row.idType?.trim().toLowerCase() || ''}|${row.idNumber?.trim().toLowerCase() || ''}`;
      if (!seen.has(key)) {
        seen.set(key, []);
      }
      seen.get(key)!.push(row);
    }

    for (const duplicates of seen.values()) {
      if (duplicates.length > 1) {
        for (const dup of duplicates) {
          this.setValidationError(dup, GlobalValidationError.DuplicateIdentification);
        }
      }
    }

    return applicationRows;
  }

  static validatePrincipalMemberRules(applicationRows: ApplicationRowDTO[]): ApplicationRowDTO[] {
    const grouped = new Map<string, ApplicationRowDTO[]>();
    for (const row of applicationRows) {
      const key = row.applicationReferenceNumber?.trim() || '';
      if (!grouped.has(key)) {
        grouped.set(key, []);
      }
      grouped.get(key)!.push(row);
    }

    for (const group of grouped.values()) {
      const principals = group.filter(row =>
        row.relationship?.toUpperCase().includes('SELF')
      );

      if (principals.length === 0) {
        for (const row of group) {
          this.setValidationError(row, GlobalValidationError.MissingPrincipal);
        }
      } else if (principals.length > 1) {
        for (const row of group) {
          this.setValidationError(row, GlobalValidationError.MultiplePrincipal);
        }
      }
    }

    return applicationRows;
  }

  static validateGeneralValidationOnAllRows(applicationRows: ApplicationRowDTO[], isDirectPayingMember: boolean): ApplicationRowDTO[] {
    for (const row of applicationRows) {
      const isPrincipalMember = ApplicationUtil.getCodeFromString(row.relationship) == 'SELF';
      const principalValidation = isPrincipalMember ? [Validators.required] : [];
      // non-direct principal members (i.e. employer group principal members) require employee number and date fields
      const employeeDetailsValidation = (isPrincipalMember && !isDirectPayingMember) ? [Validators.required] : [];

      const staticFieldValidators: { [key: string]: ValidatorFn[] } = {
        applicationReferenceNumber: [Validators.required],
        relationship: [Validators.required],
        title: [Validators.required],
        name: [Validators.required],
        surname: [Validators.required],
        idType: [Validators.required],
        dob: [Validators.required],
        gender: [Validators.required],
        maritalStatus: [Validators.required],
        ethnicity: [Validators.required],
        contactNumber: [Validators.required, CustomValidator.numeric, CustomValidator.mobileNumberValidator],
        emailAddress: [Validators.required, CustomValidator.email],
        bankName: principalValidation,
        accountNumber: principalValidation,
        accountType: principalValidation,
        addressType: [Validators.required],
        addressNumber: [Validators.required],
        addressName: [Validators.required],
        addressSuburb: [Validators.required],
        option: principalValidation,
        employeeNumber: employeeDetailsValidation,
        employmentDate: employeeDetailsValidation,
      };

      // Dynamic ID number validation
      const idType = row.idType?.trim().toUpperCase();
      if (idType?.includes('SID')) {
        staticFieldValidators.idNumber = [
          Validators.required,
          CustomValidator.idNumberNumeric,
          CustomValidator.idNumberLength,
          CustomValidator.idNumberDOB,
          CustomValidator.idLuhnValidation,
        ];
      } else {
        staticFieldValidators.idNumber = [Validators.required];
      }

      // Dynamic income validation
      if (isPrincipalMember && ApplicationUtil.getCodeFromString(row.option) === 'BCAP') {
        staticFieldValidators.incomeAmount = [
          Validators.required,
          CustomValidator.numeric,
        ];
      }

      if (row.dob && !this.isValidDateFormat(row.dob)) {
        this.setValidationError(row, GlobalValidationError.DateValidation);
      }

      if (row.employmentDate && !this.isValidDateFormat(row.employmentDate)) {
        this.setValidationError(row, GlobalValidationError.DateValidation);
      }

      // Apply validators
      for (const [field, validators] of Object.entries(staticFieldValidators)) {
        const value = (row as any)[field];
        for (const validator of validators) {
          const control = { value } as AbstractControl;
          const error = validator(control);
          if (error) {
            this.setValidationError(row, GlobalValidationError.GeneralValidation);
            break;
          }
        }
      }
    }

    return applicationRows;
  }

  static validateReferenceData(applicationRows: ApplicationRowDTO[], referenceData: ApplicationRowReferenceData): ApplicationRowDTO[] {
    for (const row of applicationRows) {
      this.setErrorOnInvalidReferenceField(referenceData.relationshipTypes, row.relationship, row);
      this.setErrorOnInvalidReferenceField(referenceData.titles, row.title, row);
      this.setErrorOnInvalidReferenceField(referenceData.idTypes, row.idType, row);
      this.setErrorOnInvalidReferenceField(referenceData.genders, row.gender, row);
      this.setErrorOnInvalidReferenceField(referenceData.maritalStatuses, row.maritalStatus, row);
      this.setErrorOnInvalidReferenceField(referenceData.ethnicities, row.ethnicity, row);
      this.setErrorOnInvalidReferenceField(referenceData.bankOptions, row.bankName, row);
      this.setErrorOnInvalidReferenceField(referenceData.accountTypes, row.accountType, row);
      this.setErrorOnInvalidReferenceField(referenceData.addressTypes, row.addressType, row);
      this.setErrorOnInvalidReferenceField(referenceData.options, row.option, row);
    }
    return applicationRows;
  }

  private static setErrorOnInvalidReferenceField(referenceDataArray: ReferenceDTO[], referenceValue: string, row: ApplicationRowDTO) {
    if (referenceDataArray && referenceValue) {
      const referenceCode = ApplicationUtil.getCodeFromString(referenceValue);
      const foundReference = referenceDataArray.find(
        (reference) => reference.key === referenceCode
      );
      if (!foundReference) {
        this.setValidationError(row, GlobalValidationError.ReferenceDataValidation);
      }
    }
  }

  static setValidationError(row: ApplicationRowDTO, error: GlobalValidationError): void {
    if (!row.validationErrors) {
      row.validationErrors = [];
    }
    if (!row.validationErrors.includes(error)) {
      row.validationErrors.push(error);
    }
  }

  private static isValidDateFormat(dateStr: string): boolean {
    if (typeof dateStr !== 'string') return false;

    const formats = [
      { format: DateUtil.dateFormat, pattern: /^(\d{4})\/(\d{2})\/(\d{2})$/ },
      { format: DateUtil.dateFormatWithHyphenated, pattern: /^(\d{4})-(\d{2})-(\d{2})$/ },
    ];

    for (const { pattern } of formats) {
      const match = dateStr.match(pattern);
      if (!match) continue;

      const year = Number(match[1]);
      const month = Number(match[2]);
      const day = Number(match[3]);

      const date = new Date(year, month - 1, day);
      if (
        date.getFullYear() === year &&
        date.getMonth() === month - 1 &&
        date.getDate() === day
      ) {
        return true;
      }
    }

    return false;
  }

}

